/**
 * ECharts Shared Configuration
 *
 * This file registers ECharts themes and provides shared configurations
 * for all chart instances across the application.
 *
 * Import this in components that use ECharts to ensure themes are registered.
 * The registration happens once on first import (module singleton pattern).
 */

import * as echarts from 'echarts';
import macaronsTheme from '../components/sales/shared/macarons.json';
import macaronsDarkTheme from '../components/sales/shared/macarons-dark.json';

// Register all themes (happens once on first import)
echarts.registerTheme('macarons', macaronsTheme);
echarts.registerTheme('macarons-dark', macaronsDarkTheme);

/**
 * Get the appropriate theme name based on app theme mode
 * @param {string} mode - 'light' or 'dark'
 * @returns {string} - Theme name for ECharts
 */
export const getEChartsTheme = (mode) => {
  return mode === 'dark' ? 'macarons-dark' : 'macarons';
};

/**
 * Common chart configuration options
 */
export const commonChartOptions = {
  textStyle: {
    fontFamily: "'IBM Plex Sans', sans-serif"
  },

  title: {
    left: 'center',
    top: 10,
    textStyle: {
      fontSize: 16,
      fontWeight: '500'
    }
  },

  // Common tooltip configuration
  tooltip: {
    trigger: 'axis',
    axisPointer: { type: 'shadow' }
  },

  // Common grid configuration
  grid: {
    left: '3%',
    right: '4%',
    bottom: '3%',
    containLabel: true
  },

  // Animation settings
  animation: true,
  animationDuration: 750,
  animationEasing: 'cubicOut'
};

/**
 * Merge common chart options with chart-specific options
 * @param {Object} chartOptions - Chart-specific options
 * @returns {Object} - Merged options
 */
export const mergeChartOptions = (chartOptions) => {
  return {
    ...commonChartOptions,
    ...chartOptions,
    title: {
      ...commonChartOptions.title,
      ...(chartOptions.title || {})
    },
    tooltip: {
      ...commonChartOptions.tooltip,
      ...(chartOptions.tooltip || {})
    },
    grid: {
      ...commonChartOptions.grid,
      ...(chartOptions.grid || {})
    }
  };
};

/**
 * Default loading options for charts
 */
export const loadingOptions = {
  text: 'Loading...',
  color: '#4A90E2',
  textColor: '#000',
  maskColor: 'rgba(255, 255, 255, 0.8)',
  zlevel: 0
};

/**
 * Responsive chart settings
 */
export const responsiveOptions = {
  lazyUpdate: true,
  notMerge: false,
  silent: false
};

/**
 * Get Line Chart Options
 * @param {Array} data - Chart data with {time, value} format
 * @param {string} title - Chart title
 * @param {string} yAxisLabel - Y-axis label
 * @param {Object} options - Optional configuration
 * @param {string} options.valueType - 'percentage', 'duration', or 'number' (default)
 * @param {Object} options.yAxisConfig - Additional y-axis configuration
 * @returns {Object} - ECharts line chart options
 */
export const getLineChartOption = (data, title, yAxisLabel, options = {}) => {
  const { valueType = 'number', yAxisConfig = {} } = options;

  // Configure formatters based on value type
  let axisFormatter, tooltipValueFormatter;
  let defaultYAxisConfig = {};

  if (valueType === 'percentage') {
    axisFormatter = (value) => (value * 100).toFixed(0) + '%';
    tooltipValueFormatter = (value) => (value * 100).toFixed(1) + '%';
    defaultYAxisConfig = { min: 0, max: 1 };
  } else if (valueType === 'duration') {
    axisFormatter = (ms) => {
      const seconds = ms / 1000;
      const mins = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      return `${mins}m ${secs}s`;
    };
    tooltipValueFormatter = (ms) => {
      const seconds = ms / 1000;
      const mins = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    };
  } else {
    axisFormatter = (value) => value.toFixed(0);
    tooltipValueFormatter = (value) => value.toFixed(2);
  }

  if (!data || data.length === 0) {
    return mergeChartOptions({
      useUTC: false,
      title: { text: title },
      xAxis: { type: 'time' },
      yAxis: {
        type: 'value',
        axisLabel: { formatter: axisFormatter },
        ...defaultYAxisConfig,
        ...yAxisConfig
      },
      series: [{ data: [], type: 'line' }]
    });
  }

  return mergeChartOptions({
    useUTC: false,  // Use local browser timezone
    title: { text: title },
    tooltip: {
      trigger: 'axis',
      formatter: (params) => {
        if (!params || params.length === 0) return '';
        const param = params[0];
        // Format timestamp in local timezone
        const date = new Date(param.value[0]);
        const timeStr = date.toLocaleString();
        return `${timeStr}<br/>${param.seriesName}: ${tooltipValueFormatter(param.value[1])}`;
      }
    },
    xAxis: {
      type: 'time',  // Use time axis for proper timestamp handling
      axisLabel: {
        rotate: 45,
        hideOverlap: true
        // ECharts automatically formats time axis labels in local timezone
      }
    },
    yAxis: {
      type: 'value',
      name: yAxisLabel,
      axisLabel: { formatter: axisFormatter },
      ...defaultYAxisConfig,
      ...yAxisConfig
    },
    series: [{
      name: yAxisLabel,
      // Data format for time axis: [timestamp, value] - keep original values
      data: data.map(item => [item.time, item.value || 0]),
      type: 'line',
      smooth: true,
      areaStyle: {
        color: {
          type: 'linear',
          x: 0, y: 0, x2: 0, y2: 1,
          colorStops: [
            { offset: 0, color: 'rgba(74, 144, 226, 0.3)' },
            { offset: 1, color: 'rgba(74, 144, 226, 0.05)' }
          ]
        }
      }
    }]
  });
};

/**
 * Get Bar Chart Options
 * @param {Array} data - Chart data
 * @param {string} title - Chart title
 * @param {string} yAxisLabel - Y-axis label
 * @param {Object} options - Optional configuration
 * @param {string} options.valueType - 'percentage', 'duration', or 'number' (default)
 * @param {Object} options.yAxisConfig - Additional y-axis configuration
 * @returns {Object} - ECharts bar chart options
 */
export const getBarChartOption = (data, title, yAxisLabel, options = {}) => {
  const { valueType = 'number', yAxisConfig = {} } = options;

  // Configure formatters based on value type
  let axisFormatter, tooltipValueFormatter;
  let defaultYAxisConfig = {};

  if (valueType === 'percentage') {
    axisFormatter = (value) => (value * 100).toFixed(0) + '%';
    tooltipValueFormatter = (value) => (value * 100).toFixed(1) + '%';
    defaultYAxisConfig = { min: 0, max: 1 };
  } else if (valueType === 'duration') {
    axisFormatter = (ms) => {
      const seconds = ms / 1000;
      const mins = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      return `${mins}m ${secs}s`;
    };
    tooltipValueFormatter = (ms) => {
      const seconds = ms / 1000;
      const mins = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    };
  } else {
    axisFormatter = (value) => value.toFixed(0);
    tooltipValueFormatter = (value) => value.toFixed(2);
  }

  if (!data || data.length === 0) {
    return mergeChartOptions({
      title: { text: title },
      xAxis: { type: 'category', data: [] },
      yAxis: {
        type: 'value',
        axisLabel: { formatter: axisFormatter },
        ...defaultYAxisConfig,
        ...yAxisConfig
      },
      series: [{ data: [], type: 'bar' }]
    });
  }

  return mergeChartOptions({
    title: { text: title },
    tooltip: {
      trigger: 'axis',
      formatter: (params) => {
        if (!params || params.length === 0) return '';
        const param = params[0];
        return `${param.name}<br/>${param.seriesName || yAxisLabel}: ${tooltipValueFormatter(param.value)}`;
      }
    },
    xAxis: {
      type: 'category',
      data: data.map(item => item.agent_name || item.agent_id || 'Unknown'),
      axisLabel: { rotate: 45, interval: 0 }
    },
    yAxis: {
      type: 'value',
      name: yAxisLabel,
      axisLabel: { formatter: axisFormatter },
      ...defaultYAxisConfig,
      ...yAxisConfig
    },
    series: [{
      name: yAxisLabel,
      data: data.map(item => item.value || 0),
      type: 'bar',
      itemStyle: {
        color: {
          type: 'linear',
          x: 0, y: 0, x2: 0, y2: 1,
          colorStops: [
            { offset: 0, color: '#4A90E2' },
            { offset: 1, color: '#357ABD' }
          ]
        }
      }
    }]
  });
};

/**
 * Get Donut Chart Options
 * @param {Array} data - Chart data
 * @param {string} title - Chart title
 * @returns {Object} - ECharts donut chart options
 */
export const getDonutChartOption = (data, title) => {
  if (!data || data.length === 0) {
    return mergeChartOptions({
      title: { text: title },
      series: [{ type: 'pie', radius: ['40%', '70%'], data: [] }]
    });
  }

  return mergeChartOptions({
    title: { text: title },
    tooltip: {
      trigger: 'item',
      formatter: '{a} <br/>{b}: {c} ({d}%)'
    },
    legend: {
      type: 'scroll',  // Make legend scrollable
      bottom: 10,
      left: 'center',
      width: '80%',  // Limit width to prevent overflow
      data: data.map(item => item.name)
    },
    grid: {
      bottom: 100  // Add space for scrollable legend
    },
    series: [{
      name: title,
      type: 'pie',
      radius: ['40%', '70%'],
      avoidLabelOverlap: false,
      itemStyle: {
        borderRadius: 10,
        borderWidth: 2,
        borderColor: '#fff'
      },
      label: {
        show: false,
        position: 'center'
      },
      emphasis: {
        label: {
          show: true,
          fontSize: '20',
          fontWeight: 'bold'
        }
      },
      labelLine: {
        show: false
      },
      data: data
    }]
  });
};

export default echarts;
